/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
'use client';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@ahafood/ui/components/avatar';
import { Badge } from '@ahafood/ui/components/badge';
import { Button } from '@ahafood/ui/components/button';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@ahafood/ui/components/card';
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from '@ahafood/ui/components/carousel';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { formatCurrency } from '@ahm/common-helpers';
import { XIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import AiGen from '@/public/static/icons/white-ai.svg';
import type { MenuItem } from '@/types/item';

interface ItemHeaderProps {
  item: MenuItem;
  storeImageUrl?: string;
  onClose: () => void;
}

export function ItemHeader({ item, storeImageUrl, onClose }: ItemHeaderProps) {
  const hasVideo = !!item.solo_meal_video_url;
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Total slides: image + video (if exists)
  const totalSlides = hasVideo ? 2 : 1;

  const onSelect = useCallback(() => {
    if (!api) return;
    const selectedIndex = api.selectedScrollSnap();
    setCurrent(selectedIndex);

    // Play video when on video slide, pause when on image slide
    if (hasVideo) {
      if (selectedIndex === 1) {
        setIsVideoPlaying(true);
        videoRef.current?.play().catch(() => {
          // Autoplay blocked
        });
      } else {
        setIsVideoPlaying(false);
        if (videoRef.current) {
          videoRef.current.pause();
          videoRef.current.currentTime = 0;
        }
      }
    }
  }, [api, hasVideo]);

  useEffect(() => {
    if (!api) return;
    api.on('select', onSelect);
    return () => {
      api.off('select', onSelect);
    };
  }, [api, onSelect]);

  const imageUrl =
    item.image_thumb_url ||
    storeImageUrl ||
    `${assetPrefix}/static/images/empty-store-bot.webp`;

  // Single image view (no carousel needed)
  if (!hasVideo) {
    return (
      <Card className="overflow-hidden relative border-none rounded-none shadow-none">
        {item.is_ai_image && (
          <Badge className="absolute bottom-2 right-2 bg-white/20 text-white border-none shadow-md px-2 py-0.5 font-bold flex items-center gap-1 text-xs">
            Hình ảnh minh hoạ bởi AI <AiGen />
          </Badge>
        )}
        <Avatar className="h-64 w-full flex-none relative rounded-none">
          <Button
            size="icon"
            variant="secondary"
            className="[&_svg]:size-6 flex-none rounded-full absolute top-4 right-4 z-10"
            onClick={onClose}
            type="button"
          >
            <XIcon />
          </Button>
          <AvatarImage
            src={imageUrl}
            alt={item.name}
            className="object-center object-cover"
          />
          <AvatarFallback className="rounded-none">{item.name}</AvatarFallback>
        </Avatar>
        <CardHeader className="p-4 w-full">
          <CardTitle className="flex flex-row items-start gap-2 justify-between text-xl">
            <h3 className="mr-auto">{item.name}</h3>
            {item.discount_config && (
              <span className="text-neutral-50 line-through text-xs font-normal mt-1.5">
                {formatCurrency(item.price)}
              </span>
            )}
            <h5>{formatCurrency(item.discounted_price ?? item.price)}</h5>
          </CardTitle>
          {item.description && (
            <CardDescription>{item.description}</CardDescription>
          )}
        </CardHeader>
      </Card>
    );
  }

  // Carousel view with image and video
  return (
    <Card className="overflow-hidden relative border-none rounded-none shadow-none">
      {item.is_ai_image && (
        <Badge className="absolute bottom-2 right-2 bg-white/20 text-white border-none shadow-md px-2 py-0.5 font-bold flex items-center gap-1 text-xs">
          Hình ảnh minh hoạ bởi AI <AiGen />
        </Badge>
      )}
      <div className="relative h-64 w-full">
        {item.is_ai_image && (
          <Badge className="absolute bottom-2 right-2 bg-white/20 text-white border-none shadow-md px-2 py-0.5 font-bold flex items-center gap-1 text-xs">
            Hình ảnh minh hoạ bởi AI <AiGen />
          </Badge>
        )}
        {/* Close button */}
        <Button
          size="icon"
          variant="secondary"
          className="[&_svg]:size-6 flex-none rounded-full absolute top-4 right-4 z-10"
          onClick={onClose}
          type="button"
          data-testid="customize-close"
        >
          <XIcon />
        </Button>

        <Carousel className="size-full" setApi={setApi}>
          <CarouselContent className="h-64">
            {/* Image slide */}
            <CarouselItem className="h-full">
              <Avatar className="relative size-full rounded-none">
                <AvatarImage
                  src={imageUrl}
                  alt={item.name}
                  className="size-full object-center object-cover"
                />
              </Avatar>
            </CarouselItem>

            {/* Video slide */}
            <CarouselItem className="h-full">
              <div className="relative size-full bg-black">
                <video
                  ref={videoRef}
                  src={item.solo_meal_video_url}
                  className="size-full object-cover"
                  muted
                  loop
                  playsInline
                  disablePictureInPicture
                  controls={false}
                  preload="metadata"
                  style={{
                    WebkitTransform: 'translateZ(0)',
                  }}
                />
              </div>
            </CarouselItem>
          </CarouselContent>
        </Carousel>

        {/* Pagination dots */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {Array.from({ length: totalSlides }).map((_, index) => (
            <button
              key={index}
              className={`h-2 rounded-full transition-all ${
                current === index
                  ? 'w-6 bg-primary'
                  : 'w-4 bg-white/80 hover:bg-white/90'
              }`}
              onClick={() => api?.scrollTo(index)}
              aria-label={index === 0 ? 'Xem ảnh' : 'Xem video'}
              type="button"
            />
          ))}
        </div>
      </div>

      <CardHeader className="p-4 w-full">
        <CardTitle className="flex flex-row items-start gap-2 justify-between text-xl">
          <h3 className="mr-auto" data-testid="customize-name-item">
            {item.name}
          </h3>
          <span
            className="text-neutral-50 line-through text-xs font-normal mt-1.5"
            data-testid="customize-origin-price"
          >
            {formatCurrency(item.price)}
          </span>
          <h5 data-testid="customize-price">
            {formatCurrency(item.discounted_price ?? item.price)}
          </h5>
        </CardTitle>
        {item.description && (
          <CardDescription data-testid="customize-desc-item">
            {item.description}
          </CardDescription>
        )}
      </CardHeader>
    </Card>
  );
}
